import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { n0 } from "../../lib/format";
import { monthLabel, payslipHtml } from "../../lib/hrDocs";
import { printHtml } from "../../lib/format";
import { Modal, Notice } from "../../lib/ui";
import "./payroll.css";

/*
 * Payroll, one screen - replaces the separate Generate / Verify / Authorise / Run pages (PayrolManagement.php,
 * payroll.php, payroll-run-view.php, run-payroll.php). Same database and the same rules as before:
 *   1 Generate   this month's rows for active staff (PAYE 10 %, pension 8 % + employer 10 %)   /api/hr/payroll/generate
 *   2 Verify     adjust bonus / deductions / advance / PAYE / pension, then verify               /api/payroll/verify
 *   3 Authorise  verified rows -> Completed + payroll payments                                   /api/hr/payroll/authorize
 *   4 Post       payroll payments -> ledger (Salary Dr 5100; Net, PAYE, Pension Cr 2000)          /api/payroll/run
 *   5 Payslips   completed rows, view or print
 * Every row starts selected, so a normal month is one click per step; the page moves to the next step itself.
 * Only Active employees are processed: disabled staff (Inactive / Resigned / Terminated) are shown but can't be selected.
 */

const STEPS = [
  ["generate", "Generate", "fa-calculator"], ["verify", "Verify", "fa-list-check"], ["authorise", "Authorise", "fa-stamp"],
  ["post", "Post to ledger", "fa-book"], ["payslips", "Payslips", "fa-file-invoice"],
];
// "Deductions" already includes PAYE + pension (as generated); PAYE and Pension are kept for the payslip and returns
const EDIT = [["bonus", "Bonus"], ["deductions", "Deductions"], ["salary_advance", "Advance"], ["paye_tax", "of which PAYE"], ["pension_contribution", "of which pension"]];

export default function PayrollCenter({ step: initial = "generate" }) {
  const api = useApi();
  const [qs, setQs] = useSearchParams();
  const step = qs.get("step") || initial;
  const go = (s) => setQs({ step: s });
  const [data, setData] = useState({});
  const [sel, setSel] = useState(new Set());
  const [opts, setOpts] = useState({});
  const [edits, setEdits] = useState({});
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    try {
      const [cand, pend, ver, done, run] = await Promise.all([
        api.get("/api/hr/payroll/candidates"), api.get("/api/hr/payroll", { status: "Pending" }), api.get("/api/hr/payroll", { status: "Verified" }),
        api.get("/api/hr/payroll", { status: "Completed" }), api.get("/api/payroll/run"),
      ]);
      setData({ cand, pend, ver, done, run });
    } catch (e) { setNotice({ tone: "err", text: e.message }); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const todo = (data.cand?.employees || []).filter((e) => !e.has_this_month);
  const runPending = (data.run || []).reduce((s, r) => s + r.count, 0);
  const counts = { generate: todo.length, verify: data.pend?.rows.length || 0, authorise: data.ver?.rows.length || 0, post: runPending, payslips: data.done?.rows.length || 0 };
  const rows = step === "generate" ? todo : step === "verify" ? data.pend?.rows || [] : step === "authorise" ? data.ver?.rows || [] : [];
  const rowId = (r) => (step === "generate" ? r.staff_id : r.payroll_id);
  const disabled = (r) => step !== "generate" && r.employee_status && r.employee_status !== "Active";
  const usable = rows.filter((r) => !disabled(r));

  // every row selected when the step (or its data) changes
  useEffect(() => {
    setSel(new Set(usable.map(rowId)));
    if (step === "generate") setOpts(Object.fromEntries(todo.map((e) => [e.staff_id, { paye: true, pension: true }])));
    setEdits({});
  }, [step, data]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOn = usable.length > 0 && sel.size === usable.length;
  const val = (r, k) => edits[r.payroll_id]?.[k] ?? String(r[k] ?? 0);
  const netPreview = (r) => Number(r.salary || 0) + Number(val(r, "bonus")) - Number(val(r, "deductions")) - Number(val(r, "salary_advance"));

  const run = async (kind) => {
    setConfirm(null); setBusy(true); setNotice(null);
    try {
      let r, next;
      if (kind === "generate") {
        r = await api.post("/api/hr/payroll/generate", { staff: [...sel].map((id) => ({ staff_id: id, ...opts[id] })) }); next = "verify";
      } else if (kind === "verify") {
        const chosen = (data.pend?.rows || []).filter((x) => sel.has(x.payroll_id))
          .map((x) => ({ payroll_id: x.payroll_id, ...Object.fromEntries(EDIT.map(([k]) => [k, Number(val(x, k)) || 0])) }));
        r = await api.post("/api/payroll/verify", { rows: chosen }); next = "authorise";
      } else if (kind === "authorise") {
        r = await api.post("/api/hr/payroll/authorize", { ids: [...sel] }); next = "post";
      } else {
        r = await api.post("/api/payroll/run", {}); next = "payslips";
      }
      setNotice({ tone: "ok", text: r.message });
      await load(); go(next);
    } catch (e) { setNotice({ tone: "err", text: e.message }); }
    finally { setBusy(false); }
  };

  const selTotal = useMemo(() => rows.filter((r) => sel.has(rowId(r))).reduce((s, r) => s + Number(step === "generate" ? r.salary : r.net_salary ?? 0), 0), [rows, sel, step]); // eslint-disable-line react-hooks/exhaustive-deps
  const ACTION = { generate: ["Generate payroll", "fa-calculator"], verify: ["Verify", "fa-check"], authorise: ["Authorise", "fa-stamp"] };

  return (
    <main className="pg" style={{ maxWidth: "96rem" }}>
      <PageHeader slug="payroll" actions={<Link to="/addEmployee" className="btn"><i className="fas fa-users" /> Employees</Link>} />
      <Notice notice={notice} onClose={() => setNotice(null)} />

      <ol className="pr-steps" aria-label="Payroll steps">
        {STEPS.map(([k, l, ic], i) => (
          <li key={k}><button type="button" aria-current={step === k ? "step" : undefined} className={step === k ? "on" : ""} onClick={() => go(k)}>
            <span className="pr-n">{i + 1}</span><span className="pr-l"><i className={`fas ${ic}`} /> {l}</span>
            <span className={`pr-c ${counts[k] ? "has" : ""}`}>{counts[k]}</span></button></li>))}
      </ol>

      {step === "generate" && data.cand && <p className="pr-hint">Month: <b>{monthLabel(data.cand.month)}</b>. {todo.length ? "Everyone below is ticked; untick anyone who shouldn't be paid this month." : "Payroll for this month has already been generated for every active employee."}</p>}
      {step === "verify" && <p className="pr-hint">Adjust any figure in place (the row stays ticked), then verify. "Deductions" is the total taken off pay and already includes PAYE and pension. Net pay shown while editing is an estimate; the database works out the final figure.</p>}
      {step === "authorise" && <p className="pr-hint">Authorising marks the rows Completed and prepares the payroll payments to post.</p>}

      {["generate", "verify", "authorise"].includes(step) && (
        <section className="panel">
          <div className="tbl-wrap"><table className="tbl">
            <thead><tr><th><input type="checkbox" aria-label="Select all" checked={allOn} onChange={(e) => setSel(e.target.checked ? new Set(usable.map(rowId)) : new Set())} /></th>
              <th>Employee</th>{step === "generate" ? <><th>Position</th><th className="num">Salary</th><th>PAYE 10%</th><th>Pension 8%</th><th className="num">Net (estimate)</th></>
                : <><th>Month</th><th className="num">Salary</th>{EDIT.map(([k, l]) => <th key={k} className="num">{l}</th>)}<th className="num">Net</th></>}</tr></thead>
            <tbody>{rows.map((r) => {
              const id = rowId(r);
              if (step === "generate") {
                const o = opts[id] || {}; const net = r.salary - (o.paye ? r.salary * 0.1 : 0) - (o.pension ? r.salary * 0.08 : 0);
                return (<tr key={id} className={sel.has(id) ? "" : "pr-off"}>
                  <td><input type="checkbox" aria-label={`Include ${r.client_name}`} checked={sel.has(id)} onChange={() => toggle(id)} /></td>
                  <td className="pr-name">{r.client_name}</td><td>{r.position}</td><td className="num mono">{n0(r.salary)}</td>
                  <td><input type="checkbox" aria-label="PAYE" checked={!!o.paye} onChange={(e) => setOpts({ ...opts, [id]: { ...o, paye: e.target.checked } })} /></td>
                  <td><input type="checkbox" aria-label="Pension" checked={!!o.pension} onChange={(e) => setOpts({ ...opts, [id]: { ...o, pension: e.target.checked } })} /></td>
                  <td className="num mono">{n0(net)}</td></tr>);
              }
              return (<tr key={id} className={sel.has(id) ? "" : "pr-off"}>
                <td><input type="checkbox" aria-label={`Select ${r.client_name}`} checked={sel.has(id)} disabled={disabled(r)} onChange={() => toggle(id)} /></td>
                <td className="pr-name">{r.client_name || `Staff #${r.staff_id}`}{disabled(r) && <span className="pill-tag err" style={{ marginLeft: ".4rem" }}>{r.employee_status}: not processed</span>}</td><td>{monthLabel(r.payment_month)}</td><td className="num mono">{n0(r.salary)}</td>
                {EDIT.map(([k]) => <td key={k} className="num">{step === "verify"
                  ? <input className="cell pr-in" type="number" min="0" step="0.01" aria-label={`${k} for ${r.client_name}`} value={val(r, k)}
                      onChange={(e) => { setEdits((x) => ({ ...x, [r.payroll_id]: { ...x[r.payroll_id], [k]: e.target.value } })); setSel((s) => new Set(s).add(r.payroll_id)); }} />
                  : <span className="mono">{n0(r[k])}</span>}</td>)}
                <td className="num mono pr-net">{n0(step === "verify" && edits[r.payroll_id] ? netPreview(r) : r.net_salary)}</td></tr>);
            })}</tbody>
          </table>{rows.length === 0 && <div className="empty"><i className="fas fa-circle-check" style={{ color: "var(--up)" }} />Nothing waiting at this step.
            {counts.verify + counts.authorise + counts.post > 0 && <> Next: <button type="button" className="lg-link" style={{ margin: 0 }} onClick={() => go(counts.verify ? "verify" : counts.authorise ? "authorise" : "post")}>continue</button></>}</div>}</div>
          {usable.length > 0 && (
            <div className="pr-bar"><span><b>{sel.size}</b> of {usable.length} selected · {step === "generate" ? "gross" : "net"} <b className="mono">TZS {n0(selTotal)}</b></span>
              <button type="button" className="btn primary" disabled={!sel.size || busy} onClick={() => setConfirm(step)}>
                <i className={`fas ${ACTION[step][1]}`} /> {busy ? "Working…" : `${ACTION[step][0]} ${sel.size}`}</button></div>)}
        </section>
      )}

      {step === "post" && (
        <section className="panel">
          <div className="tbl-wrap"><table className="tbl">
            <thead><tr><th>Payroll item</th><th>Ledger</th><th className="num">Payments</th><th className="num">Amount</th></tr></thead>
            <tbody>{(data.run || []).map((r) => <tr key={r.detail}><td>{r.detail.replace("payroll (", "").replace(")", "")}</td><td className="mono">{r.side} {r.account}</td>
              <td className="num mono">{r.count}</td><td className="num mono">{n0(r.total)}</td></tr>)}</tbody>
          </table>{runPending === 0 && <div className="empty"><i className="fas fa-circle-check" style={{ color: "var(--up)" }} />Nothing waiting to post.</div>}</div>
          {runPending > 0 && <div className="pr-bar"><span>{runPending} payroll payment{runPending === 1 ? "" : "s"} ready</span>
            <button type="button" className="btn primary" disabled={busy} onClick={() => setConfirm("post")}><i className="fas fa-book" /> {busy ? "Posting…" : "Post to ledger"}</button></div>}
        </section>
      )}

      {step === "payslips" && (
        <section className="panel">
          <div className="tbl-wrap"><table className="tbl">
            <thead><tr><th>Employee</th><th>Month</th><th className="num">Gross</th><th className="num">Net pay</th><th /></tr></thead>
            <tbody>{(data.done?.rows || []).map((r) => <tr key={r.payroll_id}><td className="pr-name">{r.client_name || `Staff #${r.staff_id}`}</td><td>{monthLabel(r.payment_month)}</td>
              <td className="num mono">{n0(Number(r.salary || 0) + Number(r.bonus || 0))}</td><td className="num mono pr-net">{n0(r.net_salary)}</td>
              <td style={{ whiteSpace: "nowrap", textAlign: "right" }}><Link className="btn sm" to={`/payslip/${r.payroll_id}`}>View</Link>{" "}
                <button type="button" className="btn sm" aria-label={`Print payslip for ${r.client_name}`}
                  onClick={async () => { try { printHtml(payslipHtml(await api.get(`/api/hr/payslips/${r.payroll_id}`))); } catch (e) { setNotice({ tone: "err", text: e.message }); } }}>
                  <i className="fas fa-print" /></button></td></tr>)}</tbody>
          </table>{(data.done?.rows || []).length === 0 && <div className="empty">No completed payroll yet.</div>}</div>
        </section>
      )}

      {confirm && (
        <Modal title={{ generate: "Generate payroll?", verify: "Verify payroll?", authorise: "Authorise payroll?", post: "Post payroll to the ledger?" }[confirm]} onClose={() => setConfirm(null)}
          actions={<><button type="button" className="btn" onClick={() => setConfirm(null)}>Cancel</button><button type="button" className="btn primary" onClick={() => run(confirm)}>Confirm</button></>}>
          {confirm === "post" ? `${runPending} payroll payments will be posted to the ledger. This can't be undone.`
            : `${sel.size} employee${sel.size === 1 ? "" : "s"}, ${confirm === "generate" ? "gross" : "net"} TZS ${n0(selTotal)}.${confirm === "authorise" ? " Authorised rows can no longer be edited." : ""}`}
        </Modal>
      )}
    </main>
  );
}
